import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building2, Mail, Phone, Store } from 'lucide-react';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { Card } from '@shared/ui/containers/Card';
import { pageCopy } from '@shared/lib/pageCopy';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { fetchSupplierGroups, type SupplierItem } from './api';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/**
 * Suppliers directory — category chips over supplier cards, mirroring the
 * Directory ("Who to call") UX. Categories are the tenant's supplier_category
 * groups; suppliers show minimal contact info (name, tap-to-call, email).
 */
export function Suppliers(): JSX.Element {
  const copy = pageCopy().suppliers;
  const [activeId, setActiveId] = useState<string | null>(null);

  const query = useQuery({ queryKey: ['suppliers'], queryFn: fetchSupplierGroups });
  const groups = query.data ?? [];
  const active = useMemo(
    () => groups.find((g) => g.id === activeId) ?? groups[0],
    [groups, activeId],
  );

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader eyebrow={copy.eyebrow} title={copy.title} subtitle={copy.subtitle} />

      {/* Category chips */}
      {groups.length > 0 ? (
        <div className="flex gap-2 overflow-x-auto px-5 pt-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {groups.map((g) => {
            const on = (active?.id ?? groups[0]?.id) === g.id;
            return (
              <button
                key={g.id}
                type="button"
                onClick={() => setActiveId(g.id)}
                className={
                  'flex shrink-0 items-center gap-1.5 rounded-pill px-4 py-2 text-body-sm font-medium transition-colors ' +
                  (on ? 'bg-primary-700 text-white' : 'bg-card hairline text-ink shadow-card')
                }
              >
                <Store
                  className={'h-4 w-4 ' + (on ? 'text-white' : 'text-ink-muted')}
                  strokeWidth={1.75}
                />
                {g.name}
              </button>
            );
          })}
        </div>
      ) : null}

      {/* Suppliers */}
      <div className="flex flex-col gap-3 px-5 pt-4 pb-safe-or-6">
        {query.isLoading ? (
          <>
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
          </>
        ) : query.isError ? (
          <ErrorState onRetry={() => query.refetch()} />
        ) : !active || active.suppliers.length === 0 ? (
          <EmptyState
            icon={<Store className="h-7 w-7" strokeWidth={1.5} />}
            title={copy.emptyTitle}
            description={copy.emptyDescription}
          />
        ) : (
          active.suppliers.map((supplier) => <SupplierCard key={supplier.id} supplier={supplier} />)
        )}
      </div>
    </div>
  );
}

function SupplierCard({ supplier }: { supplier: SupplierItem }): JSX.Element {
  return (
    <Card asChild noGrain className="rounded-xl p-4">
      <article>
        <div className="flex items-start gap-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-pill bg-primary-50">
            <span className="text-body-sm font-semibold text-primary-700">{initials(supplier.name)}</span>
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-display text-headline leading-tight text-ink">{supplier.name}</p>
          </div>
          {supplier.phone ? (
            <a
              href={`tel:${supplier.phone}`}
              aria-label={`Call ${supplier.name}`}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-primary-700 text-white"
            >
              <Phone className="h-4.5 w-4.5" strokeWidth={1.75} />
            </a>
          ) : (
            <Building2 className="h-5 w-5 shrink-0 text-ink-muted" strokeWidth={1.5} />
          )}
        </div>

        {supplier.phone || supplier.email ? (
          <div className="mt-3 flex flex-col gap-1.5">
            {supplier.phone ? (
              <a
                href={`tel:${supplier.phone}`}
                className="flex items-center gap-2 text-body-sm font-semibold text-primary-700"
              >
                <Phone className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                {supplier.phone}
              </a>
            ) : null}
            {supplier.email ? (
              <a
                href={`mailto:${supplier.email}`}
                className="flex items-center gap-2 text-body-sm text-ink-secondary"
              >
                <Mail className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.5} />
                <span className="truncate">{supplier.email}</span>
              </a>
            ) : null}
          </div>
        ) : null}
      </article>
    </Card>
  );
}
